import React, {
    CSSProperties,
    JSXElementConstructor,
    MutableRefObject,
    ReactNode,
    cloneElement,
    isValidElement,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { uniqueId } from 'lodash-es';
import { components, FocusEventHandler, Styles } from 'react-select';
import { FilterOptionOption } from 'react-select/src/filters';
import {
    ArrowIcon,
    AsyncCreatableSelectWrapper,
    AsyncSelectWrapper,
    CreatableSelectWrapper,
    CustomIconContainer,
    getCustomStyles,
    IconSpan,
    LabelSpan,
    PencilIcon,
    SelectionDescription,
    StyledClearIndicatorContainer
} from './HBDropdown.styles';
import { TScope } from '../../tokens/types';
import HBDropdownNoOptions from './HBDropdownNoOptions';
import { vars } from '../../theme';
import { TColorKeys } from '../../styles/colors';
import type {
    TOptionValue,
    HBDropdownTheme,
    HBDropdownOption,
    HBDropdownPlacement,
    TDropdownSize,
    HBDropdownAlignment
} from './HBDropdown.types';
import { ICustomThemeInput } from '../HBTextField/HBTextField.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { XCircleFilled16, IconProps } from '@honeybook/icons';
import { addTestIdToInnerProps, getSelectionFitKey } from './HBDropdown.utils';
import {
    MORE_VALUES_TEST_ID_SUFFIX,
    SELECTED_SUMMARY_TEST_ID_SUFFIX,
    SELECTION_DESCRIPTION_TEST_ID_SUFFIX
} from './HBDropdown.constants';
import createOptionComponent, {
    HighlightSearchTextProps
} from './createOptionComponent';
import { HBList } from '../HBList/HBList';
import HBBox from '../HBBox/HBBox';
import { TSemanticTextColorKeys, TTextType } from '../HBText/HBText';
import { useHBTranslation } from '../HBUIProvider/HBUIProvider';
import {
    FIT_ITEM_ATTR,
    FIT_OVERFLOW_ATTR,
    useFitItems
} from '../common/internal/useFitItems';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';

const sizeToTextType: Record<TDropdownSize, TTextType> = {
    sm: 'body14',
    md: 'body16',
    lg: 'body18',
    xl: 'body24',
    hg: 'body24'
};
interface IDropdownBase<Value = TOptionValue> {
    /** Focuses the dropdown control on mount. */
    autoFocus?: boolean;
    /** Allows backspace to remove the last selected value in multi-select mode. Note that the last value can be one hidden behind the "+N" chip, so the user can't see what was removed. Pass `false` if that matters. */
    backspaceRemovesValue?: boolean;
    /** Blurs the search input after an option is selected. */
    blurInputOnSelect?: boolean;
    /** Caches async-loaded options keyed by input value to avoid re-fetching. */
    cacheOptions?: boolean;
    /** Color of the checkmark icon shown on the selected option row. */
    checkIconColor?: string;
    /** CSS class applied to the root element. */
    className?: string;
    /**
     * Closes the dropdown menu when the parent scroll container scrolls.
     * `true` only fires on document-level scroll (not useful inside a modal — see below).
     * Use a function `() => true` to close on any scroll, including inside an `HBModal` body.
     * The boolean form does not trigger for non-document scroll containers (e.g. `overflow-y: auto` divs).
     *
     * Ignored when `portalMenu` is set — `portalMenu` installs its own scroll handler
     * that closes the menu only on scrolls outside the portaled menu.
     */
    closeMenuOnScroll?: boolean | ((event: Event) => boolean);
    /** Controls whether the menu closes after each selection. Defaults to true for single-select, false for multi-select. */
    closeMenuOnSelect?: boolean;
    /** Custom icon rendered inside the control to the left of the selected value. */
    customIconOnControl?: (props: IconProps) => JSX.Element;
    /** Replaces the default dropdown menu container component. */
    customMenuComponent?: JSXElementConstructor<any>;
    /** Replaces the inner list wrapper inside the dropdown menu. */
    customMenuListComponent?: JSXElementConstructor<any>;
    /** Replaces the default option row component. Use an HBListItem inside to preserve accessibility. */
    customOptionComponent?: JSXElementConstructor<any>;
    /** Replaces the single-value display component shown in the control when an option is selected. */
    customSingleValueComponent?: JSXElementConstructor<any>;
    /** Overrides styles for specific react-select inner components; use keys from the react-select Styles spec. */
    customStyles?: Partial<Record<keyof Styles, any>>;
    /** Overrides control background, font color, border color, and border radius. Use when embedding on non-white backgrounds such as dark member-branded lead forms. */
    customTheme?: ICustomThemeInput;
    /** Replaces the value container component that wraps the selected tags in multi-select mode. Replaces the fit-to-width chip row, so no "+N" chip or "N selected" summary is shown. */
    customValueComponent?: JSXElementConstructor<any>;
    /** @deprecated No longer active. The clear indicator is rendered by the built-in ClearIndicator component and cannot be overridden via this prop. */
    customClearIndicatorComponent?: JSXElementConstructor<any>;
    /** Replaces the "no options" empty-state message component. */
    customNoOptionsComponent?: JSXElementConstructor<any>;
    /** Opens the menu immediately on first render without user interaction. */
    defaultMenuIsOpen?: boolean;
    /** Initial set of options to show in async mode before the user types; pass true to trigger loadOptions with an empty query on mount. */
    defaultOptions?: HBDropdownOption<Value>[] | boolean;
    /** Disables user interaction and applies a not-allowed cursor. */
    disabled?: boolean;
    /** Custom color for the dropdown arrow indicator; falls back to customTheme.fontColor then neutral900. */
    dropdownIndicatorColor?: string;
    /** Visual style of the control: 'default' renders a bordered box; 'plain' removes fill; 'underline' shows only a bottom border; 'transparent' removes all borders. */
    dropdownTheme?: HBDropdownTheme;
    /** Applies error styling (red border) to the control. When used via HBFormDropdownField, managed automatically by Formik. */
    error?: boolean;
    /** Custom function to filter options based on the search input; replaces the default label-match filter. */
    filterOption?: (
        option: FilterOptionOption<HBDropdownOption>,
        inputValue: string
    ) => boolean;
    /** Inline CSS styles applied to the control when it is focused. */
    focusStyles?: CSSProperties;
    /** Custom renderer for the "Create new" option label shown at the bottom of the list when isCreatable is true. */
    formatCreateLabel?: (inputValue: string) => ReactNode | string;
    /** Custom renderer for each option row; called with the full HBDropdownOption object. */
    formatOptionLabel?: (option: HBDropdownOption<Value>) => ReactNode | string;
    /** HTML id for the dropdown's container, not the input (a `<label htmlFor>` must target the input: use `inputId`, or `inputProps.id`). Also used as the react-select name attribute. */
    id?: string;
    /**
     * HTML id set on the underlying `<input>`, so a `<label htmlFor>` associates
     * with it. Use with HBFieldControl: pass `inputId={control.id}`. Wins over
     * `inputProps.id`. Omitted: `inputProps.id`, else react-select generates the
     * input id.
     */
    inputId?: string;
    /** Accessible name for the input when the field has no visible label (e.g. an HBFieldControl with `ariaLabel`). Wins over `inputProps['aria-label']`. Omitted: `inputProps['aria-label']`, else no attribute. */
    'aria-label'?: string;
    /** ID of the element that names the input (e.g. an HBFieldControl label). Wins over `inputProps['aria-labelledby']`. Omitted: no attribute. */
    'aria-labelledby'?: string;
    /**
     * Space-separated IDs of hint/error elements describing the input. Applied
     * to the `<input>` after render, since react-select 4 does not forward it.
     * Omitted: no attribute.
     */
    'aria-describedby'?: string;
    /**
     * Marks the input invalid for assistive tech. `aria-invalid="true"` is set
     * when this or `error` is true. Applied after render, since react-select 4
     * does not forward it. Omitted: follows `error` only.
     */
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
    /** Attributes for the underlying input element. Only `id`, `aria-label` and `aria-labelledby` reach it, react-select drops the rest. */
    inputProps?: Record<string, any>;
    /** Controlled value of the search input; use together with onInputChange for external control of the search query. */
    inputValue?: string;
    /** Shows an ×-button to clear the current selection. Use for optional fields where no selection is a valid state. */
    isClearable?: boolean;
    /** Allows users to type a new value not in the list. Pair with onCreateOption to persist the new entry. */
    isCreatable?: boolean;
    /** Loads options asynchronously as the user types. Provide loadOptions to fetch from an API. */
    isAsync?: boolean;
    /** Callback that returns true for options that should be shown but not selectable. */
    isOptionDisabled?: (selectedOption: HBDropdownOption<Value>) => boolean;
    /** Enables text filtering of options as the user types. Recommended for lists of 10 or more options. */
    isSearchable?: boolean;
    /** Async function called with the current search string to fetch options from an API; used when `isAsync` is true. Return a promise resolving to an option array. */
    loadOptions?: (
        inputValue: string,
        callback?: (options: HBDropdownOption<Value>[]) => void
    ) => Promise<HBDropdownOption<Value>[] | undefined | null>;
    /** Maximum height of the dropdown menu before it scrolls; defaults to '300px'. */
    maxMenuHeight?: CSSProperties['maxHeight'];
    /** Minimum height of the dropdown menu. */
    minMenuHeight?: CSSProperties['minHeight'];
    /** Controlled open/closed state of the menu; omit to let HBDropdown manage it internally. */
    menuIsOpen?: boolean;
    /** Where the menu opens relative to the control: 'top', 'bottom', or 'auto'. Default is 'auto'. */
    menuPlacement?: HBDropdownPlacement;
    /** Horizontal alignment of the menu relative to the control: 'start' (default) or 'end'. */
    menuAlign?: HBDropdownAlignment;
    /** Scrolls the page so the menu is visible after opening; useful in long forms. */
    menuShouldScrollIntoView?: boolean;
    /** Renders the dropdown menu in a portal attached to this element. Use document.body when the dropdown is inside an overflow:hidden or CSS transform container. */
    menuPortalTarget?: HTMLElement;
    /** Sets a minimum CSS width on the control. */
    minWidth?: string;
    /** Hides the dropdown indicator arrow entirely. */
    noIndicator?: boolean;
    /** Button label inside the empty-state panel; used with customNoOptionsComponent. */
    noOptionsButtonText?: string;
    /** Explanatory text inside the empty-state panel when no options match. */
    noOptionsExplanationText?: string;
    /** Custom renderer for the "no options" message shown when the filtered list is empty. */
    noOptionsMessage?: ({
        inputValue
    }: {
        inputValue: string;
    }) => ReactNode | string;
    /** Text color key for the no-options empty-state message. */
    noOptionsColor?: TColorKeys | TSemanticTextColorKeys;
    /** Fires when the control loses focus. For creatable dropdowns, blurring with text in the input creates (and optionally selects) a new option before this fires. */
    onBlur?: FocusEventHandler;
    /** Called with the new option label string when a user creates an option via isCreatable. Async functions are awaited before the dropdown updates. */
    onCreateOption?: (optionName: string) => void | Promise<void>;
    /** When true, automatically selects the newly created option after onCreateOption resolves. */
    shouldSelectOnCreate?: boolean;
    /** Called when the control gains focus. */
    onFocus?: FocusEventHandler;
    /** Called when the dropdown menu opens. */
    onMenuOpen?: () => void;
    /** Called when the dropdown menu closes. */
    onMenuClose?: () => void;
    /** Opens the menu automatically when the control receives focus. */
    openMenuOnFocus?: boolean;
    /** Array of selectable options. Each option requires a `value` (string | number) and `label`. Use nested `options` arrays on an item for grouped sections. */
    options: HBDropdownOption<Value>[];
    /** Border color of the control in its default (unfocused) state; defaults to neutral200. */
    outlineColor?: string;
    /** Placeholder text or element shown when no value is selected. */
    placeholder?: ReactNode | string;
    /** Visual scope context ('builder' or 'system') that adjusts the color palette. */
    scope?: TScope;
    /** Size of the control, which maps to a typography scale: sm→body14, md→body16, lg→body18, xl/hg→body24. Defaults to sm. */
    size?: TDropdownSize;
    /** Opts specific react-select component types into the HBDropdown style overrides; use when customOptionComponent or customSingleValueComponent need HBDropdown's base styles. */
    stylesToInherit?: ('option' | 'SingleValue')[];
    /** Replaces the arrow indicator with a pencil icon; intended for inline editable fields. */
    usePencilIndicator?: boolean;
    /** Ref forwarded to the underlying react-select instance for programmatic control (e.g., focus, blur). */
    dropdownRef?: MutableRefObject<HTMLElement>;
    /** Custom validation for the "create new" option; return true to show the create option for the current input. Defaults to checking that no exact label or value match exists. */
    isValidNewOption?: (
        inputValue: string,
        value: Value,
        options: HBDropdownOption<Value>[]
    ) => boolean;
    /** Highlights matching characters in option labels as the user types; pass an options object to customize highlight color and style. */
    highlightSearchText?: boolean | HighlightSearchTextProps;
    /** Removes already-selected options from the menu list in multi-select mode. */
    hideSelectedOptions?: boolean;
    /** Called with the current input string on each keystroke; use with inputValue for external search state control. */
    onInputChange?: (inputValue: string) => void;
    /** Optional header and footer rendered inside the menu, outside the options list. Use listFooter for an "Add new" action at the bottom. */
    slots?: {
        listHeader?: () => ReactNode;
        listFooter?: () => ReactNode;
    };
    /**
     * Renders the dropdown menu in a `document.body` portal and closes it on any
     * ancestor scroll. Use inside `HBModal`, `HBDrawer`, `HBSidePanel`, or any
     * container with `overflow: hidden` / `overflow-y: auto` — without this prop
     * the menu is clipped at the container boundary.
     *
     * Equivalent to `menuPortalTarget={document.body}` + `menuPosition="fixed"` +
     * a `closeMenuOnScroll` function that closes only when the scroll happens outside
     * the portaled menu. The function form is required: the boolean form only fires for
     * document-level scroll and does not close the menu when an inner container
     * (e.g. the HBModal body) scrolls, causing the portaled menu to drift off the control.
     */
    portalMenu?: boolean;
}

export interface IMultiDropdown<Value = TOptionValue>
    extends IDropdownBase<Value> {
    /**
     * Enables multi-select mode; the dropdown accepts and returns an array of values.
     * The control keeps its normal height and shows one row of chips: as many as fit,
     * then a non-removable "+N" chip (click it to open the menu). When not even
     * the first chip fits next to it, an "N selected" summary replaces the chips.
     * Hidden values stay selected and can be unchecked in the menu or cleared with `isClearable`.
     */
    isMulti: true;
    /** Array of currently selected values. */
    value: Value[];
    /** Called with the full array of selected values after each selection or removal. */
    onChange: (selectedOptions: Value[]) => void;
}

export interface IDropdown<Value = TOptionValue> extends IDropdownBase<Value> {
    /** Single-select mode (default). Omit or pass false to use single-select. */
    isMulti?: false;
    /** The currently selected value. Pass `undefined` to show the placeholder. */
    value?: Value;
    /** Called with the selected option's value (string or number) when the user makes a selection. Not called when the selection is cleared — use `isClearable` and check for `undefined` after clear. */
    onChange: (selectedOption: Value) => void;
}

export const createDefaultDropdownIndicator = (
    dropdownIndicatorColor: string = vars.colors.text.primary
) => {
    return props => {
        return (
            <components.DropdownIndicator {...props}>
                <ArrowIcon
                    color={
                        props.isDisabled
                            ? vars.colors.text.disabled
                            : dropdownIndicatorColor
                    }
                />
            </components.DropdownIndicator>
        );
    };
};

export const PencilDropdownIndicator = props => {
    return (
        <components.DropdownIndicator {...props}>
            <PencilIcon
                color={vars.colors.text.tertiary}
                hoverColor={vars.colors.text.secondary}
            />
        </components.DropdownIndicator>
    );
};

const createSingleValueComponent = CustomSingleValueComponent => {
    return props => {
        const { children } = props;

        if (!CustomSingleValueComponent) {
            return <DefaultSingleValue {...props} />;
        }

        return (
            <components.SingleValue {...props}>
                <CustomSingleValueComponent {...props}>
                    {children}
                </CustomSingleValueComponent>
            </components.SingleValue>
        );
    };
};

const DefaultSingleValue = props => {
    const { icon, label } = props.data;
    const size: TDropdownSize = props.selectProps?.size || 'sm';
    const textType = sizeToTextType[size];
    const fontColor = props.selectProps?.customTheme?.fontColor;
    const Icon = iconProps => icon(iconProps);
    return (
        <>
            {icon && (
                <IconSpan>
                    <Icon color={fontColor || vars.colors.text.primary} />
                </IconSpan>
            )}
            <LabelSpan $hasIcon={!!icon} $fontColor={fontColor} type={textType}>
                {label}
            </LabelSpan>
        </>
    );
};

const ClearIndicator = props => {
    return (
        <StyledClearIndicatorContainer {...props}>
            <XCircleFilled16 color={vars.colors.text.tertiary} />
        </StyledClearIndicatorContainer>
    );
};

const createComponent = (
    name: string,
    CustomComponent: JSXElementConstructor<any>,
    testId?: string,
    CustomIconOnControl?: (props: IconProps) => JSX.Element
) => {
    const Component = components[name];

    const customIconWithContainer = CustomIconOnControl && (
        <CustomIconContainer>
            <CustomIconOnControl />
        </CustomIconContainer>
    );

    return ({ children, ...props }) => {
        return (
            <Component {...addTestIdToInnerProps(props, testId, name)}>
                {CustomComponent ? (
                    <CustomComponent {...props}>
                        {customIconWithContainer}
                        {children}
                    </CustomComponent>
                ) : CustomIconOnControl ? (
                    <>
                        {customIconWithContainer} {children}
                    </>
                ) : (
                    children
                )}
            </Component>
        );
    };
};

const NoRemove = () => null;

// Room kept free for typing when the control is searchable.
const SEARCH_INPUT_RESERVED_WIDTH = 48;

// Names listed in the accessible label of the "+N" chip and the summary.
const MAX_NAMED_VALUES = 5;

const FitMultiValue = props => (
    <components.MultiValue {...props} innerProps={{ [FIT_ITEM_ATTR]: '' }} />
);

// Single-row multi-select value area. Shows the chips that fit, then a
// non-removable "+N" chip. When not even the first chip fits next to it,
// shows an "N selected" summary instead. See `useFitItems` for the measuring.
const FitValueContainer = ({ children, ...props }) => {
    const t = useHBTranslation();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [descriptionId] = useState(() => uniqueId('hb-dropdown-selection-'));
    const { size, customTheme, isSearchable, hbTestId, hbAriaDescribedBy } =
        props.selectProps;
    const selected: HBDropdownOption[] = props.getValue();
    const total = selected.length;

    const fitCount = useFitItems({
        containerRef,
        itemCount: total,
        itemsKey: getSelectionFitKey(size, selected),
        reservedWidth: isSearchable ? SEARCH_INPUT_RESERVED_WIDTH : 0
    });

    // The ref goes on both branches: `useFitItems` subscribes to resize once,
    // on mount, so a dropdown that starts empty needs it too.
    if (!props.hasValue) {
        return (
            <components.ValueContainer
                {...props}
                innerProps={{ ref: containerRef }}
            >
                {children}
            </components.ValueContainer>
        );
    }

    const [chips, input] = children;
    const isMeasuring = fitCount === null;
    const visibleCount = isMeasuring ? total : fitCount;
    const hiddenCount = total - visibleCount;
    const isSummaryShown = !isMeasuring && visibleCount === 0;
    // While measuring, the widest possible "+N" chip stands in for the real one.
    const moreCount = isMeasuring ? total - 1 : hiddenCount;
    const isMoreChipShown = !isSummaryShown && moreCount > 0;
    const summaryText = t('HBDropdown.selectedValues', { count: total });
    const moreText = t('HBDropdown.moreValues', { count: moreCount });
    // The chip text is short to save width. The label keeps the full wording.
    const moreChipText = t('HBDropdown.moreValuesChip', { count: moreCount });

    // The "+N" chip and the summary stand in for values the user can't
    // see, so they name them for assistive tech and as a tooltip.
    // Option labels can be JSX, which has no text to read out, so then the
    // count alone is the description.
    const describeValues = (summary: string, values: HBDropdownOption[]) => {
        if (values.some(option => typeof option.label !== 'string')) {
            return summary;
        }
        const names = values
            .slice(0, MAX_NAMED_VALUES)
            .map(option => String(option.label))
            .join(', ');
        const rest = values.length - MAX_NAMED_VALUES;
        return t('HBDropdown.valuesLabel', {
            summary,
            values:
                rest > 0
                    ? `${names}, ${t('HBDropdown.andMoreValues', {
                          count: rest
                      })}`
                    : names
        });
    };

    const summaryLabel = describeValues(summaryText, selected);
    const moreLabel = describeValues(moreText, selected.slice(visibleCount));

    // Focus lands on the input, so the whole selection, including what the
    // "+N" chip stands in for, is announced as its description. The count
    // prefix keeps the text from matching a single option label exactly.
    const selectionDescription =
        isSummaryShown ||
        hiddenCount === 0 ||
        selected.some(option => typeof option.label !== 'string')
            ? summaryLabel
            : t('HBDropdown.valuesLabel', {
                  summary: summaryText,
                  values: [
                      selected
                          .slice(0, visibleCount)
                          .map(option => String(option.label))
                          .join(', '),
                      moreLabel
                  ].join(', ')
              });

    return (
        <components.ValueContainer
            {...props}
            innerProps={{ ref: containerRef }}
        >
            {isSummaryShown ? (
                <HBBox
                    displayName="SelectedValuesSummary"
                    role="group"
                    minWidth="0"
                    aria-label={summaryLabel}
                    title={summaryLabel}
                >
                    <LabelSpan
                        type={sizeToTextType[size as TDropdownSize]}
                        $fontColor={customTheme?.fontColor}
                        testId={`${hbTestId}-${SELECTED_SUMMARY_TEST_ID_SUFFIX}`}
                    >
                        {summaryText}
                    </LabelSpan>
                </HBBox>
            ) : (
                chips.slice(0, visibleCount)
            )}
            {isMoreChipShown && (
                <components.MultiValue
                    {...props}
                    components={{
                        Container: components.MultiValueContainer,
                        Label: components.MultiValueLabel,
                        Remove: NoRemove
                    }}
                    innerProps={{
                        'data-testid': `${hbTestId}-${MORE_VALUES_TEST_ID_SUFFIX}`,
                        role: 'group',
                        'aria-label': moreLabel,
                        title: moreLabel,
                        ...(isMeasuring && { [FIT_OVERFLOW_ATTR]: '' })
                    }}
                >
                    {moreChipText}
                </components.MultiValue>
            )}
            {isValidElement(input)
                ? cloneElement(input as React.ReactElement, {
                      'aria-describedby': [hbAriaDescribedBy, descriptionId]
                          .filter(Boolean)
                          .join(' ')
                  })
                : input}
            <SelectionDescription
                id={descriptionId}
                data-testid={`${hbTestId}-${SELECTION_DESCRIPTION_TEST_ID_SUFFIX}`}
            >
                {isMeasuring ? '' : selectionDescription}
            </SelectionDescription>
        </components.ValueContainer>
    );
};

// Defined at module scope so the component reference is stable across renders.
// The container ref is threaded in via `menuPortalContainerRef` on selectProps.
const PortalMenuPortal = (props: any) => {
    const DefaultMenuPortal = components.MenuPortal;
    return (
        <DefaultMenuPortal {...props}>
            <div ref={props.selectProps.menuPortalContainerRef}>
                {props.children}
            </div>
        </DefaultMenuPortal>
    );
};

const HBDropdown = <Value extends TOptionValue>({
    autoFocus,
    backspaceRemovesValue,
    blurInputOnSelect,
    cacheOptions,
    checkIconColor = vars.colors.text.accent,
    className,
    closeMenuOnScroll,
    closeMenuOnSelect,
    customIconOnControl,
    customMenuComponent,
    customMenuListComponent,
    customOptionComponent,
    customSingleValueComponent,
    customStyles,
    customTheme,
    customValueComponent,
    customNoOptionsComponent,
    defaultMenuIsOpen,
    shouldSelectOnCreate,
    defaultOptions,
    disabled = false,
    dropdownIndicatorColor,
    dropdownTheme = 'default',
    error = false,
    filterOption,
    focusStyles,
    formatCreateLabel,
    formatOptionLabel,
    id,
    inputId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    testIdName,
    testId,
    inputProps,
    inputValue,
    isClearable = false,
    isCreatable = false,
    isAsync = false,
    isOptionDisabled,
    isSearchable = false,
    loadOptions,
    maxMenuHeight = '300px',
    minMenuHeight,
    menuIsOpen,
    menuPlacement = 'bottom',
    menuAlign = 'start',
    menuShouldScrollIntoView,
    menuPortalTarget,
    minWidth,
    noIndicator = false,
    noOptionsButtonText,
    noOptionsExplanationText,
    noOptionsColor,
    noOptionsMessage,
    onBlur,
    onChange,
    onCreateOption,
    onFocus,
    onMenuOpen,
    onMenuClose,
    openMenuOnFocus,
    options,
    outlineColor = vars.colors.stroke.secondary,
    placeholder = 'Select an option',
    scope,
    size = 'sm',
    stylesToInherit = [],
    usePencilIndicator = false,
    value = null,
    isValidNewOption,
    dropdownRef,
    highlightSearchText,
    hideSelectedOptions = false,
    onInputChange,
    slots,
    portalMenu,
    ...props
}: (IDropdown<Value> | IMultiDropdown<Value>) &
    MustIncludeOneOfBasicTestIdWithIdProps) => {
    const styles = getCustomStyles({
        size,
        scope,
        dropdownTheme,
        ignoreStylesOf: {
            option:
                !!customOptionComponent && !stylesToInherit.includes('option'),
            SingleValue:
                !!customSingleValueComponent &&
                !stylesToInherit.includes('SingleValue')
        },
        customStyles,
        customTheme,
        focusStyles,
        error,
        outlineColor,
        withIcon: !!customIconOnControl,
        menuAlign,
        hasCustomMenu: !!customMenuComponent,
        maxMenuHeight
    });

    let selected = null;
    if (props.isMulti) {
        selected = value;
    } else {
        options.forEach(item => {
            if (item.options) {
                item.options.forEach(option => {
                    if (option.value === value) {
                        selected = option;
                    }
                });
            }
            if (item.value === value && !item.isFakeOption) {
                selected = item;
            }
        });
    }
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'dropdown',
        componentName: 'HBDropdown'
    });

    const clearInputValue = () => {
        // Clear the input value to prevent handleBlur from creating a duplicate option
        // when user selects an existing option after typing a search query
        const dropdownElement = id
            ? document.getElementById(id)
            : document.querySelector(`[data-testid="${finalTestId}"]`);
        if (dropdownElement) {
            const inputElement = dropdownElement.querySelector('input');
            if (inputElement) {
                inputElement.value = '';
            }
        }
    };

    const isInvalid = isAriaInvalid(error, ariaInvalid);

    const resolvedInputId = inputId ?? inputProps?.id;
    const appliedDescribedBy = useRef<string[]>([]);

    // react-select 4 forwards only aria-label/aria-labelledby/aria-autocomplete
    // to its input, so aria-describedby and aria-invalid are set on the element
    // directly. No dependency array: react-select can remount the input (e.g.
    // DummyInput vs search input), so the attributes are reapplied every render.
    // aria-describedby is merged, not overwritten: a multi-select's "N selected"
    // description (see FitValueContainer) lives on the same input, so only the
    // ids this effect added last time are replaced.
    useEffect(() => {
        const inputElement = resolvedInputId
            ? document.getElementById(resolvedInputId)
            : id
            ? document.getElementById(id)?.querySelector('input')
            : null;
        if (!inputElement) {
            return;
        }
        const own = ariaDescribedBy?.split(/\s+/).filter(Boolean) ?? [];
        const foreign = (inputElement.getAttribute('aria-describedby') ?? '')
            .split(/\s+/)
            .filter(
                token =>
                    token &&
                    !appliedDescribedBy.current.includes(token) &&
                    !own.includes(token)
            );
        appliedDescribedBy.current = own;
        const describedBy = [...own, ...foreign].join(' ');
        if (describedBy) {
            inputElement.setAttribute('aria-describedby', describedBy);
        } else {
            inputElement.removeAttribute('aria-describedby');
        }
        if (isInvalid) {
            inputElement.setAttribute('aria-invalid', 'true');
        } else {
            inputElement.removeAttribute('aria-invalid');
        }
    });

    const handleAsyncChange = item => {
        // Clear input to prevent blur from creating duplicate option
        clearInputValue();

        if (item && item.isFakeOption && item.handleFakeOptionClick) {
            return item.handleFakeOptionClick();
        }

        return onChange(item);
    };

    const handleChange = (
        item: HBDropdownOption<Value> | HBDropdownOption<Value>[]
    ) => {
        // Clear input to prevent blur from creating duplicate option
        clearInputValue();

        if (props.isMulti) {
            //@ts-ignore
            return onChange(item);
        }
        //@ts-ignore
        if (item && item.isFakeOption && item.handleFakeOptionClick) {
            //@ts-ignore
            return item.handleFakeOptionClick();
        }
        //@ts-ignore
        return onChange(item?.value);
    };

    const DropdownIndicator = useMemo(() => {
        if (noIndicator) {
            return null;
        }
        if (usePencilIndicator) {
            return PencilDropdownIndicator;
        }
        return createDefaultDropdownIndicator(
            dropdownIndicatorColor ?? customTheme?.fontColor
        );
    }, [
        dropdownIndicatorColor,
        customTheme?.fontColor,
        noIndicator,
        usePencilIndicator
    ]);

    const Option = useMemo(
        () =>
            createOptionComponent({
                customOptionComponent,
                checkIconColor,
                finalTestId,
                size,
                highlightSearchText,
                customTheme
            }),
        [
            checkIconColor,
            customOptionComponent,
            finalTestId,
            size,
            highlightSearchText,
            customTheme
        ]
    );

    const SingleValue = useMemo(
        () => createSingleValueComponent(customSingleValueComponent),
        [customSingleValueComponent]
    );

    const Menu = useMemo(
        () =>
            createComponent(
                'Menu',
                customMenuComponent
                    ? customMenuComponent
                    : ({ children }) => (
                          <HBList
                              disableKeyboardNavigation
                              slots={{
                                  header: slots?.listHeader?.(),
                                  footer: slots?.listFooter?.()
                              }}
                          >
                              {children}
                          </HBList>
                      ),
                finalTestId
            ),
        [customMenuComponent, finalTestId, slots]
    );

    const MenuList = useMemo(
        () => createComponent('MenuList', customMenuListComponent),
        [customMenuListComponent]
    );

    const menuPortalContainerRef = useRef<HTMLDivElement | null>(null);

    const ValueContainer = useMemo(
        () =>
            props.isMulti && !customValueComponent
                ? FitValueContainer
                : createComponent('ValueContainer', customValueComponent),
        [customValueComponent, props.isMulti]
    );

    const NoOptionsMessage = useMemo(
        () =>
            createComponent(
                'NoOptionsMessage',
                customNoOptionsComponent,
                noOptionsColor
            ),
        [customNoOptionsComponent, noOptionsColor]
    );

    const NoOptionsPanel = useMemo(() => {
        return (
            formatCreateLabel ||
            (searchValue => {
                return (
                    <HBDropdownNoOptions
                        value={searchValue}
                        noOptionsButtonText={noOptionsButtonText}
                        dropdownNoOptionsExplanation={noOptionsExplanationText}
                        color={noOptionsColor}
                    />
                );
            })
        );
    }, [
        formatCreateLabel,
        noOptionsButtonText,
        noOptionsColor,
        noOptionsExplanationText
    ]);

    const Control = useMemo(
        () =>
            createComponent('Control', null, finalTestId, customIconOnControl),
        [finalTestId, customIconOnControl]
    );

    const Input = useMemo(
        () => createComponent('Input', null, finalTestId),
        [finalTestId]
    );

    const getCreateOptionResult = async newOption => {
        if (!onCreateOption) {
            return Promise.resolve();
        }

        const result = onCreateOption(newOption);

        if (result instanceof Promise) {
            await result;
        }

        return Promise.resolve();
    };

    const handleOptionCreation = async newOption => {
        if (onCreateOption) {
            await getCreateOptionResult(newOption);
            if (shouldSelectOnCreate) {
                if (isAsync) {
                    handleAsyncChange({ value: newOption, label: newOption });
                } else {
                    onChange(newOption);
                }
            }
        }
    };

    const handleBlur = e => {
        onBlur?.(e);

        // this is to cause the option to be created and selected when the user clicks outside the dropdown
        const newOption = e.target.value;
        if (newOption) {
            handleOptionCreation(newOption);
        }
    };

    function getDefaultInputValue() {
        return typeof value === 'object' && !Array.isArray(value)
            ? (value as HBDropdownOption<Value> | null)?.label
            : value;
    }

    const commonProps = {
        id,
        name: id,
        // react-select has no `inputProps`; these are the input attributes it forwards.
        inputId: resolvedInputId,
        'aria-label': ariaLabel ?? inputProps?.['aria-label'],
        'aria-labelledby': ariaLabelledBy ?? inputProps?.['aria-labelledby'],
        customTheme,
        menuPortalTarget: portalMenu ? document.body : menuPortalTarget,
        menuPosition: portalMenu ? 'fixed' : undefined,
        menuPortalContainerRef: portalMenu ? menuPortalContainerRef : undefined,
        minWidth,
        options,
        styles,
        size,
        hbTestId: finalTestId,
        hbAriaDescribedBy: ariaDescribedBy,
        isMulti: props.isMulti,
        isSearchable,
        isDisabled: disabled,
        placeholder,
        menuPlacement,
        menuShouldScrollIntoView,
        maxMenuHeight,
        minMenuHeight,
        onMenuOpen: () => !disabled && onMenuOpen?.(),
        onMenuClose: () => !disabled && onMenuClose?.(),
        className,
        classNamePrefix: 'hb-dropdown',
        components: {
            Control,
            Input,
            Option,
            DropdownIndicator,
            SingleValue,
            ValueContainer,
            ...(props.isMulti &&
                !customValueComponent && {
                    MultiValue: FitMultiValue
                }),
            NoOptionsMessage,
            ClearIndicator,
            Menu,
            MenuList,
            ...(portalMenu && { MenuPortal: PortalMenuPortal })
        },
        onBlur: handleBlur,
        onFocus,
        menuIsOpen,
        defaultMenuIsOpen,
        closeMenuOnScroll: portalMenu
            ? (event: Event) => {
                  if (!menuPortalContainerRef.current) {
                      return false;
                  }
                  return !menuPortalContainerRef.current.contains(
                      event.target as Node
                  );
              }
            : closeMenuOnScroll,
        isOptionDisabled,
        onCreateOption: handleOptionCreation,
        isClearable,
        isValidNewOption:
            isValidNewOption ||
            function (inputValueText) {
                const hasExactMatch = options.some(
                    option =>
                        option.label === inputValueText ||
                        option.value === inputValueText
                );

                return isCreatable && !hasExactMatch && inputValueText;
            },
        closeMenuOnSelect: closeMenuOnSelect ?? !props.isMulti,
        inputValue,
        onInputChange,
        blurInputOnSelect,
        formatCreateLabel: NoOptionsPanel,
        formatOptionLabel: formatOptionLabel,
        noOptionsMessage,
        filterOption,
        autoFocus,
        openMenuOnFocus,
        backspaceRemovesValue,
        inputProps: {
            ...inputProps,
            autoComplete: 'nope'
        },
        ref: dropdownRef,
        selectProps: {
            hideSelectedOptions: props.isMulti ? hideSelectedOptions : undefined
        },
        hideSelectedOptions: props.isMulti ? hideSelectedOptions : undefined
    };

    if (isCreatable && isAsync) {
        return (
            <AsyncCreatableSelectWrapper
                {...commonProps}
                value={value}
                options={options}
                defaultOptions={defaultOptions}
                loadOptions={loadOptions}
                cacheOptions={cacheOptions}
                onChange={handleAsyncChange}
            />
        );
    }

    return isAsync ? (
        <AsyncSelectWrapper
            {...commonProps}
            value={value}
            defaultInputValue={getDefaultInputValue()}
            defaultOptions={defaultOptions}
            loadOptions={loadOptions}
            cacheOptions={cacheOptions}
            onChange={handleAsyncChange}
        />
    ) : (
        <CreatableSelectWrapper
            {...commonProps}
            value={selected}
            options={options}
            onChange={handleChange}
        />
    );
};

export default HBDropdown;
